/**
 * GP portal API layer. Calls ONLY /v2/gp/* endpoints with the GP token.
 * Intentionally NOT wired into the Redux store — the GP world is isolated
 * from the normal app's state, components and API surface.
 */
import { baseURL } from '../../constant';
import { getGpToken, clearGpSession } from '../../Utils/gpAuth';

const GP = {
  LOGIN: `${baseURL}/gp/auth/login`,
  REPORTS: `${baseURL}/gp/reports`,
  REPORT: (id: string) => `${baseURL}/gp/reports/${encodeURIComponent(id)}`,
  REPORT_PDF: (id: string) => `${baseURL}/gp/reports/${encodeURIComponent(id)}/pdf`,
  REPORT_LITE_IMAGES: (id: string) => `${baseURL}/gp/reports/${encodeURIComponent(id)}/lite-images`,
  REPORT_MEASUREMENTS: (id: string) => `${baseURL}/gp/reports/${encodeURIComponent(id)}/measurements`,
};

export interface GpLoginResult {
  token: string;
  user: { _id: string; name: string; email?: string; providerNumber: string; role: 'GP' };
}

export interface GpReportListItem {
  _id: string;
  patientName: string | null;
  dob: string | null;
  sex: string | null;
  patientId: string | null;
  reportName: string | null;
  studyType: 'echo' | 'stressEcho';
  studyDate: string | null;
  studyTime: string | null;
  modality: string | null;
  studyInstanceUID: string | null;
  hospitalId: string | null;
  clinicName: string | null;
  finalizedBy: string | null;
}

export interface GpReportDetail extends GpReportListItem {
  age?: string | number | null;
  numberOfImages?: number;
}

export interface GpPagination {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface GpLiteInstance {
  stage?: string;
  view?: string;
  instanceUID?: string;
  mediaUrl?: string;
  instanceNumber?: string;
  seriesNumber?: string;
}

export type GpMeasurementCategory = '2D' | 'MMODE' | 'DOPPLER' | 'STRAIN' | 'DERIVED';

export interface GpMeasurement {
  measurementId: string;
  parameterName: string;
  category: GpMeasurementCategory;
  value: number;
  unit: string | null;
  method: string | null;
  planeView: string | null;
  normalRange: { low?: number; high?: number; indexed?: boolean } | null;
  isAbnormal: boolean;
  loincCode: string | null;
}

export interface GpMeasurementsResult {
  reportId: string;
  studyInstanceUID: string | null;
  status: string | null;
  measurements: GpMeasurement[];
}

export interface GpListFilters {
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  studyType?: 'echo' | 'stressEcho' | '';
  page?: number;
  limit?: number;
}

async function gpFetch(url: string, options: RequestInit = {}): Promise<any> {
  const token = getGpToken();
  const response = await fetch(url, {
    ...options,
    headers: {
      ...(options.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (response.status === 403) {
    // expired/invalid GP token → force re-login
    clearGpSession();
    window.location.href = '/gp/login';
    throw new Error('Session expired');
  }
  const result = await response.json();
  if (!response.ok) {
    throw new Error(result?.msg || `Request failed: ${response.status}`);
  }
  return result;
}

export async function gpLogin(providerNumber: string, password: string): Promise<GpLoginResult> {
  const response = await fetch(GP.LOGIN, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ providerNumber, password }),
  });
  const result = await response.json();
  if (!response.ok) {
    throw new Error(result?.msg || `Login failed: ${response.status}`);
  }
  return result.result as GpLoginResult;
}

export async function gpListReports(
  filters: GpListFilters = {}
): Promise<{ data: GpReportListItem[]; pagination: GpPagination }> {
  const params = new URLSearchParams();
  if (filters.search) params.set('search', filters.search);
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);
  if (filters.studyType) params.set('studyType', filters.studyType);
  params.set('page', String(filters.page || 1));
  params.set('limit', String(filters.limit || 20));
  const result = await gpFetch(`${GP.REPORTS}?${params.toString()}`);
  return result.result;
}

export async function gpGetReport(reportId: string): Promise<GpReportDetail> {
  const result = await gpFetch(GP.REPORT(reportId));
  return result.result as GpReportDetail;
}

export async function gpGetReportPdfBlobUrl(reportId: string): Promise<string> {
  const token = getGpToken();
  const response = await fetch(GP.REPORT_PDF(reportId), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (response.status === 403) {
    clearGpSession();
    window.location.href = '/gp/login';
    throw new Error('Session expired');
  }
  if (!response.ok) {
    throw new Error(`Failed to load report PDF: ${response.status}`);
  }
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

export async function gpGetReportLiteImages(
  reportId: string
): Promise<{ studyInstanceUID: string | null; instances: GpLiteInstance[] }> {
  const result = await gpFetch(GP.REPORT_LITE_IMAGES(reportId));
  return result.result;
}

export async function gpGetReportMeasurements(reportId: string): Promise<GpMeasurementsResult> {
  const result = await gpFetch(GP.REPORT_MEASUREMENTS(reportId));
  return result.result as GpMeasurementsResult;
}
